import { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { agentAccess, citizenAccess } from "../../components/Utils";
import { FarmerProvider } from "../context/FarmerContext";
import FarmerContextRoute from "./FarmerContextRoute";
import { carriedTrail, useArrivalBreadcrumb, withArrivalTrail } from "../contexts/useArrivalBreadcrumb";
import { homeBreadcrumbEntry } from "../contexts/breadcrumbStackUtils";
import RewampPageShell from "../components/RewampPageShell";
import AgentIdentityPicker from "../components/AgentIdentityPicker";
import { FARMER_VERIFY_PAGE_SPEC } from "../pages/specs/farmerVerifyPageSpec";

export const IDENTITY_MODE = Object.freeze({ SELF: "self", FARMER: "farmer" });

const IdentityModeContext = createContext(null);

/** { mode, setMode } for an AGENT on a self-or-farmer route; null for everyone else. */
export const useIdentityMode = () => useContext(IdentityModeContext);

// Router-state value that sends the AGENT back to the account picker.
const PICK_MODE = "pick";
const PICKER_FALLBACK_ANCESTORS = [homeBreadcrumbEntry()];

const noop = () => {};

const readUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("DfsWeb.user-info")) ?? {};
  } catch {
    return {};
  }
};

// The logged-in AGENT in the Farmer shape, so pages built on useFarmer() work unchanged.
const toSelfFarmer = (user) => ({
  name: user.name ?? "",
  mobileNumber: user.mobileNumber ?? user.userName ?? null,
  dbtId: null,
  individual: { userUuid: user.uuid ?? null, userId: user.id ?? null, address: [] },
});

function AgentSelfIdentity() {
  const parentOutletContext = useOutletContext();
  const value = useMemo(
    () => ({
      role: "AGENT",
      identity: IDENTITY_MODE.SELF,
      farmer: toSelfFarmer(readUserInfo()),
      resourceTokenId: null,
      resourcePayload: null,
      refresh: noop,
      invalidate: noop,
    }),
    []
  );
  return (
    <FarmerProvider value={value}>
      <Outlet context={parentOutletContext} />
    </FarmerProvider>
  );
}

// Mounted as a layout route, so the choice survives moving between the listing, raise and ticket pages.
function AgentIdentityChoice({ selfCrumb, categoryType }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const value = useMemo(() => ({ mode, setMode }), [mode]);

  // The verify step's Back / parent crumb lands on the same URL with `identityMode`; consume it once so
  // a later history Back onto that entry can't flip the mode again.
  const requestedMode = location.state?.identityMode;
  useEffect(() => {
    if (!requestedMode) return;
    setMode(requestedMode === PICK_MODE ? null : requestedMode);
    const { identityMode, ...rest } = location.state;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: rest });
  }, [requestedMode, location, navigate]);

  const arrivalTrail = carriedTrail(location.state);
  const crumbId = selfCrumb?.id;
  const crumbRoute = selfCrumb?.route;
  const crumbLabelKey = selfCrumb?.labelKey;
  const verifyParent = useMemo(
    () =>
      crumbId && {
        id: crumbId,
        route: crumbRoute,
        labelKey: crumbLabelKey,
        state: withArrivalTrail({ identityMode: PICK_MODE }, arrivalTrail),
      },
    [crumbId, crumbRoute, crumbLabelKey, arrivalTrail]
  );

  useArrivalBreadcrumb(selfCrumb, PICKER_FALLBACK_ANCESTORS, { enabled: mode == null });

  if (mode == null) {
    return (
      <RewampPageShell spec={FARMER_VERIFY_PAGE_SPEC} breadcrumb>
        <AgentIdentityPicker onSelect={setMode} />
      </RewampPageShell>
    );
  }

  return (
    <IdentityModeContext.Provider value={value}>
      {mode === IDENTITY_MODE.SELF ? <AgentSelfIdentity /> : <FarmerContextRoute verifyParent={verifyParent} categoryType={categoryType} />}
    </IdentityModeContext.Provider>
  );
}

AgentIdentityChoice.propTypes = {
  selfCrumb: PropTypes.shape({ id: PropTypes.string, route: PropTypes.string, labelKey: PropTypes.string }),
  categoryType: PropTypes.string,
};

// CITIZEN loads their own profile; AGENT first picks an account: Self, or verify a farmer (`categoryType`) and act for them.
// `selfCrumb` is the route's crumb; the farmer-verify step steps back to the picker on it.
export default function SelfIdentityRoute({ selfCrumb, categoryType }) {
  return agentAccess() && !citizenAccess() ? (
    <AgentIdentityChoice selfCrumb={selfCrumb} categoryType={categoryType} />
  ) : (
    <FarmerContextRoute />
  );
}

SelfIdentityRoute.propTypes = {
  selfCrumb: PropTypes.shape({ id: PropTypes.string, route: PropTypes.string, labelKey: PropTypes.string }),
  /** AGENT farmer-verify categoryType; FarmerContextRoute's PROFILE_GET default when omitted. */
  categoryType: PropTypes.string,
};
